import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container-main py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-8">Last updated: January 2026</p>

          <div className="prose prose-slate max-w-none">
            <h2 className="text-xl font-semibold mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              SpotFinder ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We collect information that you provide directly to us:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4">
              <li><strong>Account Information:</strong> Name, email address, password, phone number</li>
              <li><strong>Profile Information:</strong> Profile photo, vehicle details</li>
              <li><strong>Payment Information:</strong> Credit card details (processed securely by Stripe)</li>
              <li><strong>Listing Information:</strong> Address, photos, pricing, availability</li>
              <li><strong>Communications:</strong> Messages between users, support inquiries</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Information Collected Automatically</h2>
            <p className="text-slate-600 mb-4">When you use our Service, we automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4">
              <li>Device information (type, operating system, browser)</li>
              <li>IP address and location data</li>
              <li>Usage data (pages visited, features used, time spent)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address fraud and security issues</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Information Sharing</h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4">
              <li><strong>Other Users:</strong> Hosts can see Driver names; Drivers can see Host names and listing details</li>
              <li><strong>Service Providers:</strong> Third parties that help us operate our Service (Stripe, email providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate security measures to protect your personal information. However, 
              no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your information for as long as your account is active or as needed to provide you 
              services. You may request deletion of your account and personal data at any time.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Export your data in a portable format</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Cookies</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar technologies to enhance your experience. You can control cookies 
              through your browser settings, but disabling them may affect functionality.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              Our Service is not intended for children under 18. We do not knowingly collect information 
              from children under 18.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this Privacy Policy, please contact us at privacy@spotfinder.co
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
